import React from "react";
import MarketingLayout from "components/layout/MarketingLayout";

export interface TocItem {
  id: string;
  label: string;
}

interface LegalPageLayoutProps {
  title: string;
  icon: React.ReactNode;
  lastUpdated?: string;
  intro?: React.ReactNode;
  tocItems?: TocItem[];
  children: React.ReactNode;
  contactEmail?: string;
  contactLabel?: string;
}

export const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  title,
  icon,
  lastUpdated,
  intro,
  tocItems = [],
  children,
  contactEmail,
  contactLabel = "Contact us",
}) => {
  return (
    <MarketingLayout>
      {/* Hero - same max-width and padding as content for aligned start position */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/90 via-white to-white" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-18">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary-100 flex items-center justify-center text-2xl shadow-soft">
              {icon}
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-secondary-900 font-display">
                {title}
              </h1>
              {lastUpdated && (
                <span className="inline-flex items-center mt-3 px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-600">
                  Last updated: {lastUpdated}
                </span>
              )}
              {intro && (
                <div className="mt-4 text-secondary-700 text-lg leading-relaxed max-w-2xl">
                  {intro}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content + TOC - same max-w and padding as hero so content starts at same position */}
      <section className="relative bg-secondary-50/40">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="lg:flex lg:gap-12 lg:items-start">
            {/* Main content - no extra padding so section cards align with hero */}
            <div className="flex-1 min-w-0">
              <div className="space-y-8 w-full">{children}</div>

              {/* Contact CTA */}
              {contactEmail && (
                <div className="mt-12 rounded-2xl bg-white border border-secondary-200 shadow-soft px-4 sm:px-6 lg:px-8 py-6 sm:py-8 text-center">
                  <p className="text-secondary-700 font-medium">
                    {contactLabel}
                  </p>
                  <a
                    href={`mailto:${contactEmail}`}
                    className="mt-2 inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold transition-colors"
                  >
                    {contactEmail}
                  </a>
                </div>
              )}
            </div>

            {/* Sticky TOC - desktop only */}
            {tocItems.length > 0 && (
              <aside className="hidden lg:block flex-shrink-0 w-56 mt-2">
                <div className="sticky top-24 rounded-xl border border-secondary-200 bg-white/80 backdrop-blur p-4 shadow-soft">
                  <p className="text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-3">
                    On this page
                  </p>
                  <nav className="space-y-1.5">
                    {tocItems.map((item) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        className="block text-sm text-secondary-600 hover:text-primary-600 transition-colors py-0.5 border-l-2 border-transparent hover:border-primary-400 pl-3 -ml-px"
                      >
                        {item.label}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>
            )}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

interface LegalSectionProps {
  id: string;
  number?: number;
  title: string;
  children: React.ReactNode;
}

export const LegalSection: React.FC<LegalSectionProps> = ({
  id,
  number,
  title,
  children,
}) => (
  <section
    id={id}
    className="rounded-2xl border border-secondary-200 bg-white shadow-soft overflow-hidden scroll-mt-24"
  >
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-7">
      <h2 className="text-xl font-semibold text-secondary-900 flex items-center gap-3">
        {number != null && (
          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold">
            {number}
          </span>
        )}
        {title}
      </h2>
      <div className="mt-4 prose prose-secondary max-w-none text-secondary-700 leading-relaxed space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_li]:leading-relaxed [&_a]:text-primary-600 [&_a]:hover:underline">
        {children}
      </div>
    </div>
  </section>
);

export default LegalPageLayout;
